import * as Commons from "oc-commons/Commons";
import { Sphere, Vector2 } from "three";
import * as ClosedInterval from "oc-commons/ClosedInterval";
import {
  extrapolateInfiniteAndMiddles,
  getOverhang,
  getOverlap,
  Interval,
  middle,
} from "oc-commons/ClosedInterval";
import * as Maths3 from "oc-threejs/Maths3";
import { LazyPool } from "oc-jaco3d/arrangement/LazyPool";
import { Aufbau } from "./Aufbau";
import { Part } from "./Part";
import { OBB } from "three/examples/jsm/math/OBB";
import { MAuswahlwert } from "../../metamodell";
import { Grundriss } from "./Grundriss";
import { Segment } from "./Segment";
import { Ecke } from "./Ecke";
import {
  getGrundrissKanteX,
  getSegmentKanteX,
  pfostenbreiteCirca,
  SegmentKante,
  updateEckenUndSegmenteFn,
} from "./grundrissFunctions";
import { Pfosten } from "./Pfosten";
import * as _ from "lodash";
//
// type SegmentKante = {
//   /* entspricht dem Index im arrangement.segmente */
//   index: number;
//   /* Start- und Endpunkt des Segmentes */
//   segmentKante: THREE.Vector3[];
//   /* Array mit Start- und Endpunkt von Empties, die wegen Aufbauten wie Wegen freigelassen werden sollen */
//   emptyKanten: THREE.Vector3[][];
// };
//
// type GrundrissKante = {
//   /* der ursprüngliche GrundrissKantenIndex */
//   streckenIndex: number;
//   /* die beiden ursprünglichen Segment-Eckpunkte */
//   grundrissKante: THREE.Vector3[];
//   /* die neuen Eckpunkte innerhalb des Segmentes nach der Unterteilung */
//   segmentKanten: SegmentKante[];
// };

export function prependHistory<T>(value: T, history: T[]) {
  return value == null ? history : [value].concat(history.filter((f) => f !== value));
}

export function löscheNachbarElementeUmMindestplatzZuErreichen(
  part: any,
  mindestbreite: number,
  uiRemove = true
) {
  const toInterval = (part): Interval =>
    part?.segment ? [part.segment.getX(part, 0), part.segment.getX(part, 1)] : null;
  let interval = toInterval(part);
  let changed = false;
  const middle = ClosedInterval.middle(interval);
  const parts = part.segment.getParts();
  const index = parts.indexOf(part);
  let linksIndex = index - 1;
  let rechtsIndex = index + 1;
  while (ClosedInterval.size(interval) < mindestbreite) {
    const links = parts[linksIndex];
    const rechts = parts[rechtsIndex];
    const unionWithLinks = ClosedInterval.union(interval, toInterval(links));
    const unionWithRechts = ClosedInterval.union(interval, toInterval(rechts));
    const linksMiddleDistance = Math.abs(ClosedInterval.middle(unionWithLinks) - middle);
    const rechtsMiddleDistance = Math.abs(ClosedInterval.middle(unionWithRechts) - middle);
    let neighbour;
    if (rechts == null || (links?.isEmpty && !rechts?.isEmpty)) {
      neighbour = links;
    } else if (links == null || (rechts?.isEmpty && !links?.isEmpty)) {
      neighbour = rechts;
    } else {
      neighbour = linksMiddleDistance < rechtsMiddleDistance ? links : rechts;
    }
    if (neighbour == null) {
      break;
    }
    if (!neighbour.isEmpty) {
      if (uiRemove) {
        neighbour.uiRemove();
        part.uiRemove();
      } else {
        neighbour.segment.remove(neighbour);
        neighbour.segment.remove(part);
      }
      changed = true;
    }
    interval = ClosedInterval.union(interval, toInterval(neighbour));
    if (links === neighbour) {
      linksIndex--;
    } else {
      rechtsIndex++;
    }
  }
  if (changed) {
    part.arrangement.update3d();
  }
}

export function getTypeNameOfPart(p) {
  return p?.isMontageplatten
    ? "Montageplatten"
    : p?.isAnschlagprofil
      ? "Anschlagprofil"
      : p?.isUMProfil
        ? "UMProfil"
        : p?.isTorpfosten || p?.isPfostenAnEcke
          ? (p?.isSchiebetorEinzelpfosten
              ? "SchiebetorEinzelpfosten"
              : p?.isSchiebetorDoppelpfosten
                ? "SchiebetorDoppelpfosten"
                : p?.isTorpfosten
                  ? "Torpfosten"
                  : "") + (p?.isPfostenAnEcke ? "Eckpfosten" : "")
          : p?.isSchiebetor
            ? "Schiebetor"
            : p?.isDoppeltor
              ? "Doppeltor"
              : p?.isTor
                ? "Einzeltor"
                : p?.isAufbau
                  ? p.aufbauMeta?.Key
                  : p.constructor.CLASS_NAME;
}

interface SchneideParams {
  segmente: Segment[];
  ecken: Ecke[];
  grundriss: Grundriss;
  aufbauten: Aufbau[];
  verteilungsstrategieAuswahlwert_: MAuswahlwert;
  partsAndPfosten: (Part | Pfosten)[];
  lazyPoolEcken: LazyPool<Ecke>;
  lazyPoolSegmente: LazyPool<Segment>;
}

/**
 * Diese Funktion übernimmt die Neuberechnung und Neuverteilung der Segmente und zugehörigen Elemente,
 * wenn sich die Geometrie des Grundrisses ändert. Sie erfüllt dabei folgende wesentliche Use Cases:
 *
 * 1. Persistente Attribute übertragen:
 *    - Benutzerdefinierte Einstellungen (z.B. Verteilungsstrategie) werden anhand der bisherigen
 *      X-Intervalle der Segmente gespeichert und auf die neuen Segmente übertragen.
 *
 * 2. Geometrieänderungen berücksichtigen:
 *    - Durch Verschieben von Eckpunkten, Hinzufügen/Entfernen von Aufbauten oder das Teilen/Verschmelzen
 *      von Segmenten ändern sich die X-Intervalle der Segmente. Diese Funktion ermittelt für jedes
 *      neue Segment anhand des berechneten X-Intervalls den besten passenden persistierten Zustand
 *      (durch Überlappungsberechnungen und Intervall-Extrapolation).
 *
 * 3. Wiederverwendung von Objekten:
 *    - Mittels Lazy Pools werden Eck- und Segmentobjekte wiederverwendet, um deren Objektidentität
 *      möglichst passend zu erhalten.
 *
 * 4. Elemente auf Segmente verteilen:
 *    - Nachdem die Segmente neu berechnet und mit persistierten Attributen versehen wurden, werden
 *      auch zugeordnete Elemente (wie Parts, Pfosten oder UM-Profile) den neuen Segmenten entsprechend
 *      zugewiesen.
 *
 * Zusammengefasst: Diese Funktion stellt sicher, dass die vom Benutzer festgelegten Segmentattribute
 * auch bei Änderungen der Grundrissgeometrie erhalten bleiben und die neu ermittelten Segmente sowie
 * zugehörige Elemente konsistent und nachvollziehbar aktualisiert werden.
 */
export function schneideEckenUndSegmente({
  segmente,
  ecken,
  grundriss,
  aufbauten,
  verteilungsstrategieAuswahlwert_,
  partsAndPfosten,
  lazyPoolEcken,
  lazyPoolSegmente,
}: SchneideParams): void {
  interface SegmentAttribute {
    verteilungsstrategieAuswahlwert: MAuswahlwert;
  }

  // wie viele Meter dürfen Elemente / Pfosten über das Segmentende hinausragen, bevor sie gelöscht werden?
  const overhangTolerance = 0.2;

  function getSegmentGrundrisskantenIntervall(segment: Segment): Interval {
    const interval = [0, segment.breite];
    if (segment.ecken.length < 2) return interval as Interval;
    return interval.map((x) => getGrundrissKanteX(segment.segmentKante, x)) as Interval;
  }

  const schneideSegmente = () => {
    // Initialisierung der Map mit dem alten Stand der Attribute pro KantenIntervall (Segment)
    const kantenIdToXIntervalsWithAttributes = (() => {
      const kantenIdToXIntervalsWithAttributes = new Map<
        number,
        [number, number, SegmentAttribute][]
      >();

      segmente.forEach((segment) => {
        const kanteId = segment.segmentKante?.grundrissKante.id;
        if (!kanteId) return;
        const interval = getSegmentGrundrisskantenIntervall(segment) as unknown as [
          number,
          number,
          SegmentAttribute,
        ];
        interval[2] = {
          // hier alle persistenten Attribute des Segmentes auflisten,
          // ausser Parts/Empties/Pfosten/etc. oder grundrissKante
          verteilungsstrategieAuswahlwert: segment.verteilungsstrategieAuswahlwert,
        };
        if (!kantenIdToXIntervalsWithAttributes.has(kanteId)) {
          kantenIdToXIntervalsWithAttributes.set(kanteId, []);
        }
        kantenIdToXIntervalsWithAttributes.get(kanteId)!.push(interval);
      });

      kantenIdToXIntervalsWithAttributes.forEach((persistentIntervals) => {
        extrapolateInfiniteAndMiddles(persistentIntervals as unknown as Interval[]);
      });

      return kantenIdToXIntervalsWithAttributes;
    })();

    function getAttributesForSegmentInterval(segment: Segment): SegmentAttribute {
      const kanteId = segment.segmentKante?.grundrissKante.id;
      if (!kanteId) return;
      const effectiveIntervals = kantenIdToXIntervalsWithAttributes.get(kanteId);
      if (!effectiveIntervals) return;
      const newInterval = getSegmentGrundrisskantenIntervall(segment);
      const bestInterval = _.maxBy(effectiveIntervals, (interval) =>
        getOverlap(interval as unknown as Interval, newInterval)
      );
      return bestInterval?.[2];
    }

    // Unterteile die GrundrissKanten in Segmente (z.B. Schnitte durch Mauer-Aufbauten)
    const { segmentKanten, mauerAufbautenObbs } = updateEckenUndSegmenteFn({
      kanten: grundriss.kanten,
      aufbauten: aufbauten,
      wegeWieMauern: false,
    });

    // Erzeuge Eck-Objekte anhand der Eckpositionen
    const eckenKanten = segmentKanten.map(
      (segmentKante: SegmentKante) =>
        ({
          segmentKante,
          ecken: segmentKante.punkte.map((positionV2: Vector2) => {
            const position = Maths3.v2To3xz(positionV2);
            const ecke = lazyPoolEcken.findOrCreate(
              (e: Ecke) => e.position.distanceTo(position) < pfostenbreiteCirca
            );
            const eckePositionMoved = ecke.position.distanceTo(position) > pfostenbreiteCirca;
            // behandle die Ecke, als sei sie neu erzeugt
            ecke.reset();
            ecke.position.copy(position);
            if (ecke.pfosten) {
              ecke.pfosten.position.copy(position);
              if (eckePositionMoved) {
                // Ecke wurde signifikant verschoben (z.B. durch changeStreckenLaenge und
                // lazyPool-Wiederverwendung). Element-Referenzen auf diesen Eckpfosten
                // aufbrechen, damit die Verteilung ihn nicht als "alten inneren Pfosten"
                // aus element.pfostenLinks/pfostenRechts rekrutiert und an eine falsche
                // Position zieht.
                ecke.pfosten.elemente.forEach((e) => {
                  if (e.pfostenLinks === ecke.pfosten) e.pfostenLinks = null;
                  if (e.pfostenRechts === ecke.pfosten) e.pfostenRechts = null;
                });
              }
              ecke.pfosten.elemente = [];
            }
            return ecke;
          }),
        }) as { segmentKante: SegmentKante; ecken: Ecke[] }
    );
    lazyPoolEcken.cleanup();

    const setAnMauerOnEcken = () => {
      ecken.forEach((ecke) => {
        const eckenSphere = new Sphere(ecke.position.clone(), pfostenbreiteCirca);
        const intersectingObbs = mauerAufbautenObbs.filter((obb: OBB) =>
          obb.intersectsSphere(eckenSphere)
        );
        ecke.anMauer = intersectingObbs.length > 0;
        ecke.aufbauten = intersectingObbs.map((o) => o.aufbau);
      });
    };
    setAnMauerOnEcken();

    const getSegmentFromPool = (() => {
      const usedSegmente = new Set<Segment>();
      const segmentKante2GrundrissKanteXIntervall = (segmentKante: SegmentKante) =>
        segmentKante.punkte.map((p) =>
          getGrundrissKanteX(segmentKante, getSegmentKanteX(segmentKante, p))
        ) as Interval;
      // Wähle ein Segment aus dem Pool mittels Ranking (Abstand der Intervallmitte)
      return (segmentKante: SegmentKante) => {
        const xa = middle(segmentKante2GrundrissKanteXIntervall(segmentKante));
        const segment = lazyPoolSegmente.pickOrCreate((poolArray: Segment[]) =>
          _.maxBy(
            poolArray.filter((s) => !usedSegmente.has(s)),
            (candidate: Segment) => {
              if (
                !candidate.segmentKante ||
                candidate.segmentKante.grundrissKante.id !== segmentKante.grundrissKante.id
              ) {
                return -Infinity;
              }
              const xb = middle(getSegmentGrundrisskantenIntervall(candidate));
              return -Math.abs(xb - xa);
            }
          )
        );
        usedSegmente.add(segment);
        return segment;
      };
    })();

    // Kanten zu Segment-Objekten
    eckenKanten.forEach(({ segmentKante, ecken }) => {
      const segment = getSegmentFromPool(segmentKante);
      // Setze die Attribute des Segmentes. Behandle es, als sei es neu erzeugt.
      segment.segmentKante = segmentKante;
      segment.ecken = ecken as [Ecke, Ecke];
      ecken.forEach((ecke) => {
        ecke.segmente.push(segment);
      });
      segment.updateCoordinateSystem();
      const attributes = getAttributesForSegmentInterval(segment);
      if (attributes) {
        const { verteilungsstrategieAuswahlwert } = attributes;
        if (
          verteilungsstrategieAuswahlwert &&
          verteilungsstrategieAuswahlwert !== verteilungsstrategieAuswahlwert_
        ) {
          segment.verteilungsstrategieAuswahlwert = verteilungsstrategieAuswahlwert;
        }
      }
    });
    lazyPoolSegmente.cleanup();
  };

  // Verteile Elemente auf Segmente
  const schneideSegmenteUndVerteileElementeAufSegmente = () => {
    // Ermittle für jedes Part das [Start, End]-Intervall (X-Positionen)
    const getGrundrissKantenIntervalX = (part: any): Interval =>
      ClosedInterval.order(
        [0, 1].map((side: number) =>
          part.segment.getGrundrissKanteX(
            part.segment.getWorldPositionOfX(part.segment.getX(part, side))
          )
        ) as Interval
      ) as Interval;
    const grundrissKantenParts = partsAndPfosten
      .filter((p: any) => !p.isPfostenAnEcke && p.parent?.isSegment)
      .map((part: Part | Pfosten) => ({
        kanteId: part.segment.segmentKante.grundrissKante.id,
        grundrissKantenIntervalX: getGrundrissKantenIntervalX(part),
        part,
      }));

    // Rufe schneideSegmente() auf, um Segmente neu zu erstellen bzw. zu aktualisieren
    // Gruppiere Segmente und Parts nach der Grundrisskante
    schneideSegmente();

    // Gruppiere Segmente und Parts nach der zugehörigen Grundrisskante
    const grundrissKantenSegmenteMap = Commons.groupBy(
      segmente,
      (s: Segment) => s.segmentKante.grundrissKante.id
    );
    const grundrissKantenPartsMap = Commons.groupBy(
      grundrissKantenParts,
      (item: any) => item.kanteId
    );

    grundriss.kanten.forEach((kante: any) => {
      const kanteId = kante.id;
      const segmenteForKante = grundrissKantenSegmenteMap.get(kanteId) ?? [];
      const partsForKante = grundrissKantenPartsMap.get(kanteId) ?? [];
      // Initialisiere die Arrays in jedem Segment
      segmenteForKante.forEach((segment: Segment) => {
        segment.parts = [];
        segment.pfosten = [];
        segment.umprofile = [];
      });
      // Verhindere Mehrfachzuordnungen
      const usedParts = new Set();
      // Für jedes Part auf dieser Kante: bestimme das Segment mit der besten Überlappung
      partsForKante.forEach(({ grundrissKantenIntervalX, part }: any) => {
        if (usedParts.has(part)) return;
        let bestSegment: Segment | null = null;
        let bestOverlap = 0;
        segmenteForKante.forEach((segment: Segment) => {
          const segmentInterval = ClosedInterval.order(
            segment.ecken.map((ecke) => segment.getGrundrissKanteX(ecke.position)) as Interval
          ) as Interval;
          const overlap = getOverlap(segmentInterval, grundrissKantenIntervalX);
          const overhang = getOverhang(segmentInterval, grundrissKantenIntervalX);
          // if (overhang <= overhangTolerance) {
          if (overlap > bestOverlap) {
            bestOverlap = overlap;
            bestSegment = segment;
          }
          // }
        });
        if (bestSegment) {
          // Je nach Typ des Parts wird er in das entsprechende Array eingefügt.
          if (part.isElement || (part.isEmpty && part.automatischeBreite === false)) {
            bestSegment.addPart(part);
          } else if (part.isPfosten && !part.isPfostenAnEcke) {
            if (!bestSegment.pfosten.includes(part)) {
              bestSegment.pfosten.push(part);
            }
          } else if (part.isUMProfil) {
            if (!bestSegment.umprofile.includes(part)) {
              bestSegment.umprofile.push(part);
            }
          }
          usedParts.add(part);
        } else {
          // debugger;
        }
      });
      // Nach der Zuordnung: Bereinige Verweise und weise den Parent zu
      segmenteForKante.forEach((segment: Segment) => {
        const partsAndPfosten = segment.getPartsAndPfosten();
        // Rausgenommen, weil es zur Fehlern bei der Wiederverwendung von Pfosten führt. Dadurch schlagen keine neuen
        // Tests fehl.. also erst mal rausnehmen
        // partsAndPfosten.forEach((p: any) => {
        //   [
        //     "pfostenRechts",
        //     "pfostenLinks",
        //     "umprofilRechts",
        //     "umprofilLinks",
        //     "nachbarLinks",
        //     "nachbarRechts",
        //   ].forEach((key) => {
        // if (p[key] != null && !partsAndPfosten.includes(p[key]) && !p[key]?.isPfostenAnEcke) {
        //   p[key] = null;
        // }
        //   });
        // });
        partsAndPfosten
          .filter((p: any) => !p.isPfostenAnEcke)
          .forEach((part: any) => {
            part.parent = segment;
          });
      });
    });
  };

  const uninitialisiert = segmente.some((segment: Segment) => segment.ecken.length === 0);
  if (uninitialisiert) {
    schneideSegmente();
  } else {
    schneideSegmenteUndVerteileElementeAufSegmente();
  }
}

/**
 * Filtert ein Array von Sets basierend auf einer unique id ihrer Elemente.
 *
 * @param sets Das Array von Sets, das gefiltert werden soll.
 * @param idOfT Funktion, die die ID eines Elements zurückgibt.
 * @returns Ein neues Array, das nur die einzigartigen Sets enthält.
 */
export function filterUniqueSetsBy<T>(sets: Set<T>[], idOfT: (t: T) => number): Set<T>[] {
  const uniqueMap = new Map<string, Set<T>>();
  for (const set of sets) {
    const ids = Array.from(set, idOfT);
    ids.sort((a, b) => a - b);
    const key = ids.join(",");
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, set);
    }
  }
  return Array.from(uniqueMap.values());
}

export const lichtleistenZubehörAnzahlKeyfilter = (key: string) =>
  key != "Edelstahl-Blende_Lichtleiste_Steckzaun" || !APP.arrangement.hasOnlyRhombusLichtleisten;
